/** Small styled building blocks shared across the Nutrition V2 screens.
 *
 *  Styled from `ui/v2Tokens` — see the note there on why V2 keeps its own palette. These are
 *  raw elements in a `Box` rather than MUI components on purpose: the design mock's controls
 *  are lighter and denser than MUI's defaults, and fighting `MuiTextField` into this shape
 *  costs more than styling an `<input>`. */

import { Box, type BoxProps } from "@mui/material";
import type { ElementType, MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { v2 } from "./v2Tokens";

/**
 * The faint grey behind inputs and stat cells in the CALCULATOR, which still runs on the
 * previous mock (`--surface: #fafafa`). Deliberately not `t.bg`, which is a bluer, heavier
 * grey. New work should reach for `v2.canvas` instead; this stays for the calculator.
 */
export const SURFACE = "#fafafa";

export const Card = ({ children, sx, ...rest }: BoxProps) => (
  <Box
    sx={{
      background: v2.surface,
      border: `1px solid ${v2.line}`,
      borderRadius: v2.rLg,
      boxShadow: v2.shadow,
      overflow: "hidden",
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

export const SectionTitle = ({ children, sx }: { children: ReactNode; sx?: BoxProps["sx"] }) => (
  <Box
    sx={{
      fontSize: "11px",
      fontWeight: 700,
      color: v2.ink3,
      textTransform: "uppercase",
      letterSpacing: ".7px",
      marginBottom: "14px",
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const Field = ({
  label,
  children,
  required,
  missing,
  sx,
}: {
  label: ReactNode;
  children: ReactNode;
  /** marks the label with a red asterisk — nothing can be computed without it */
  required?: boolean;
  /** required AND still empty: the label and the control itself turn red */
  missing?: boolean;
  /** mostly for `gridColumn` when the field sits in a grid and spans more than one cell */
  sx?: BoxProps["sx"];
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "5px",
      // the control is a child primitive, so the empty-required state is styled from here
      ...(missing
        ? {
            "& input, & select": {
              borderColor: v2.over,
              background: v2.overWash,
              "&::placeholder": { color: v2.over, opacity: 0.7 },
            },
          }
        : {}),
      ...sx,
    }}
  >
    <Box sx={{ fontSize: "12.5px", fontWeight: 500, color: missing ? v2.over : v2.ink2 }}>
      {label}
      {required && (
        <Box component="span" aria-hidden sx={{ color: v2.over, marginInlineStart: "3px" }}>
          *
        </Box>
      )}
    </Box>
    {children}
  </Box>
);

/** A one-line explanation under a field. `warn` for "this doesn't add up", not for an error. */
export const Hint = ({ children, warn }: { children: ReactNode; warn?: boolean }) => (
  <Box sx={{ fontSize: "11.5px", color: warn ? v2.warn : v2.ink4 }}>{children}</Box>
);

const controlSx = {
  width: "100%",
  boxSizing: "border-box" as const,
  height: "38px",
  padding: "0 11px",
  borderRadius: v2.rSm,
  border: `1px solid ${v2.line}`,
  background: v2.surface,
  color: v2.ink,
  fontSize: "14px",
  fontFamily: "inherit",
  outline: "none",
  transition: "border-color .12s, box-shadow .12s",
  "&:focus": { borderColor: v2.brand, boxShadow: `0 0 0 3px ${v2.brandWash}` },
};

export const Select = ({
  value,
  onChange,
  options,
  disabled,
  ariaLabel,
  sx,
}: {
  value: string | number;
  onChange: (value: string) => void;
  options: Array<{ value: string | number; label: string }>;
  disabled?: boolean;
  ariaLabel?: string;
  sx?: BoxProps["sx"];
}) => (
  <Box
    component="select"
    aria-label={ariaLabel}
    value={value}
    disabled={disabled}
    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
    sx={{ ...controlSx, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, ...sx }}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </Box>
);

export const TextInput = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
  sx,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  sx?: BoxProps["sx"];
}) => (
  <Box
    component="input"
    type="text"
    aria-label={ariaLabel}
    value={value}
    placeholder={placeholder}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    sx={{ ...controlSx, "&::placeholder": { color: v2.ink4 }, ...sx }}
  />
);

export const NumberInput = ({
  value,
  onChange,
  placeholder,
  min,
  max,
  step,
  ariaLabel,
  suffix,
  sx,
}: {
  value: number | string;
  onChange: (value: string) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  ariaLabel?: string;
  suffix?: string;
  sx?: BoxProps["sx"];
}) => (
  <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
    <Box
      component="input"
      type="number"
      aria-label={ariaLabel}
      value={value}
      placeholder={placeholder}
      min={min}
      max={max}
      step={step}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      sx={{
        ...controlSx,
        fontVariantNumeric: "tabular-nums",
        paddingInlineEnd: suffix ? "44px" : "11px",
        "&::placeholder": { color: v2.ink4 },
        ...sx,
      }}
    />
    {suffix && (
      <Box
        sx={{
          position: "absolute",
          insetInlineEnd: "11px",
          fontSize: "12px",
          color: v2.ink4,
          pointerEvents: "none",
        }}
      >
        {suffix}
      </Box>
    )}
  </Box>
);

/**
 * A value you edit in place, with no field around it — the menu title, a meal's name.
 *
 * The affordance is the point: it looks like text until you approach it, then it looks like an
 * input. Three screens hand-rolled their own version of this before it lived here, and they
 * had drifted apart on radius, weight and what focus looks like.
 */
export const InlineEdit = ({
  value,
  onChange,
  ariaLabel,
  placeholder,
  size = "md",
  autoFocus,
  onBlur,
  sx,
}: {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
  /** `lg` is a page title, `md` a meal name, `sm` an alternative's name */
  size?: "sm" | "md" | "lg";
  /**
   * For a field that only exists because the trainer asked for it. Pressing "rename" and then
   * having to click the box you just summoned is a step that says nothing.
   */
  autoFocus?: boolean;
  /** Leaving the field is how a summoned one closes again — see `SubstitutionsPanel`. */
  onBlur?: () => void;
  sx?: BoxProps["sx"];
}) => {
  const sizes = {
    sm: { fontSize: "13.5px", fontWeight: 600, pad: "2px 6px", radius: v2.rSm },
    md: { fontSize: "15.5px", fontWeight: 600, pad: "2px 6px", radius: v2.rSm },
    lg: { fontSize: "22px", fontWeight: 600, pad: "2px 8px", radius: v2.rSm },
  }[size];

  return (
    <Box
      component="input"
      type="text"
      aria-label={ariaLabel}
      value={value}
      placeholder={placeholder}
      autoFocus={autoFocus}
      onBlur={onBlur}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      sx={{
        fontSize: sizes.fontSize,
        fontWeight: sizes.fontWeight,
        letterSpacing: "-.01em",
        fontFamily: "inherit",
        color: v2.ink,
        // the negative margin cancels the padding, so the text sits exactly where plain text
        // would — the control grows outwards on hover instead of shifting the line
        padding: sizes.pad,
        margin: `-${sizes.pad.split(" ")[0]} -${sizes.pad.split(" ")[1]}`,
        borderRadius: sizes.radius,
        border: "1px solid transparent",
        background: "transparent",
        outline: "none",
        minWidth: 0,
        transition: "border-color .12s, background .12s",
        "&:hover": { borderColor: v2.line, background: v2.surface },
        "&:focus": { borderColor: v2.brand, background: v2.surface },
        "&::placeholder": { color: v2.ink4, fontWeight: 400 },
        ...sx,
      }}
    />
  );
};

export const TextButton = ({
  children,
  onClick,
  tone = "neutral",
  quiet,
  disabled,
  sx,
  ...rest
}: {
  children: ReactNode;
  /**
   * Takes the event, so a button inside a clickable row can stop the click reaching it. Without
   * that, a toggle nested in a row that also handles clicks re-opens itself the moment it closes.
   */
  onClick?: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  tone?: "neutral" | "green" | "danger" | "purple";
  /** drops the border — for a cancel sitting next to a real button */
  quiet?: boolean;
  disabled?: boolean;
} & Omit<BoxProps, "onClick">) => {
  const tones = {
    neutral: { color: v2.ink2, border: v2.line, hoverBg: v2.hover, hoverBorder: "#D8DEE5" },
    green: { color: v2.brand, border: "rgba(1,187,167,.35)", hoverBg: v2.brandWash, hoverBorder: v2.brand },
    danger: { color: v2.over, border: "rgba(214,72,59,.3)", hoverBg: v2.overWash, hoverBorder: v2.over },
    // the AI treatment from the mock — the one button on the page that isn't the brand
    purple: { color: "#5B4BA8", border: "#DDD6F3", hoverBg: "#F1EEFC", hoverBorder: "#CFC5EE" },
  }[tone];

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        // 44 on a phone: a button that stands on its own line is the case the iOS HIG's 44pt is
        // actually about, and every caller that wants it shorter overrides `height` in its own sx.
        height: { xs: "44px", md: "36px" },
        padding: "0 14px",
        borderRadius: v2.rSm,
        border: quiet ? "1px solid transparent" : `1px solid ${tones.border}`,
        background: quiet ? "transparent" : tone === "purple" ? "#F7F5FE" : v2.surface,
        color: tones.color,
        fontSize: "13.5px",
        fontWeight: 500,
        fontFamily: "inherit",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap",
        transition: "background .12s, border-color .12s, color .12s",
        "&:hover": disabled
          ? {}
          : { background: quiet ? v2.lineSoft : tones.hoverBg, borderColor: quiet ? "transparent" : tones.hoverBorder },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export const PrimaryButton = ({
  children,
  onClick,
  disabled,
  sx,
  ...rest
}: { children: ReactNode; onClick?: () => void; disabled?: boolean } & Omit<BoxProps, "onClick">) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "7px",
      // matches TextButton — see the note there on 44 vs 36
      height: { xs: "44px", md: "36px" },
      padding: "0 14px",
      borderRadius: v2.rSm,
      border: "none",
      background: disabled ? v2.line : v2.brand,
      color: disabled ? v2.ink4 : "#fff",
      fontSize: "13.5px",
      fontWeight: 500,
      fontFamily: "inherit",
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "background .12s",
      "&:hover": disabled ? {} : { background: v2.brandDark },
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

export const Pill = ({
  children,
  active,
  onClick,
  ariaPressed,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  /**
   * Opt-in `aria-pressed`, for a pill that is genuinely a toggle in a set — the food log's metric
   * switcher, where a screen reader otherwise hears four identical buttons with no way to tell
   * which one the calendar is currently showing.
   *
   * Not derived from `active`: most pills here are filters or navigation, and announcing those as
   * pressed toggles would be worse than saying nothing. The caller knows which it is.
   */
  ariaPressed?: boolean;
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-pressed={ariaPressed}
    sx={{
      padding: "6px 14px",
      borderRadius: "20px",
      fontSize: "13.5px",
      fontWeight: active ? 600 : 500,
      fontFamily: "inherit",
      cursor: "pointer",
      color: active ? v2.brandText : v2.ink2,
      border: `1px solid ${active ? v2.brand : v2.line}`,
      background: active ? v2.brandWash : v2.surface,
      transition: "all .12s",
      "&:hover": { borderColor: active ? v2.brand : v2.ink4 },
    }}
  >
    {children}
  </Box>
);

/** A control with a small caption above it — a bare number next to a bare dropdown
 *  gives the trainer nothing to read. */
export const Labeled = ({
  label,
  children,
  hideLabel,
  sx,
}: {
  label: string;
  children: ReactNode;
  /**
   * Keeps the control's accessible name but drops the printed caption.
   *
   * For a row that REPEATS a labelled row above it — an alternative under the food it
   * replaces — the caption is answered by the row it copies, and printing it again is both
   * noise and a claim that these two rows are peers.
   */
  hideLabel?: boolean;
  /**
   * Mostly `flex`: this wraps the control, so a caller that wants the CONTROL to grow has to be
   * able to say so here — otherwise the wrapper stays at its content width and the control inside
   * has nothing to fill.
   */
  sx?: BoxProps["sx"];
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", ...sx }}>
    {!hideLabel && (
      <Box sx={{ fontSize: "9.5px", fontWeight: 600, color: v2.ink4, textAlign: "center" }}>{label}</Box>
    )}
    {children}
  </Box>
);

export const EmptyState = ({
  icon: Icon,
  text,
  children,
  compact,
}: {
  icon?: ElementType;
  text: string;
  children?: ReactNode;
  /**
   * One quiet line instead of a centred block.
   *
   * An empty state earns a whole panel when it is the ONLY thing on screen — a menu with no
   * meals, a dialog with nothing to pick. It does not when it is one row inside a card the
   * trainer is already working in: an empty meal was spending as much height as a meal with
   * three foods to say it had none, and a menu is built by adding meals first.
   */
  compact?: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: compact ? "row" : "column",
      alignItems: "center",
      justifyContent: "center",
      gap: compact ? "7px" : "10px",
      padding: compact ? "11px 16px" : "44px 24px",
      color: v2.ink3,
      textAlign: "center",
    }}
  >
    {Icon && <Icon sx={{ fontSize: compact ? 17 : 34, color: "#CFD8E0" }} />}
    <Box sx={{ fontSize: compact ? "12.5px" : "13.5px", maxWidth: "38ch", lineHeight: compact ? 1.4 : 1.6 }}>
      {text}
    </Box>
    {children}
  </Box>
);

/**
 * The builder's floating message — "נשמר", or a save that failed.
 *
 * Pinned to the INLINE-END corner, which in this RTL app is the bottom left. It used to sit at
 * inline-start, i.e. bottom right, which is exactly where the side nav is: the toast slid under the
 * drawer and the trainer never saw that their menu had saved. The z-index is above
 * `layout.zIndex.sideNavMobile` (1300) for the same reason — at 1300 the mobile drawer won on DOM
 * order alone.
 *
 * A failed save is deliberately NOT transient. The menu exists only in this tab, so the message has
 * to stay until the trainer does something about it rather than fade after three seconds; that is
 * what `onDismiss` is for, and why the error tone carries `role="alert"`.
 */
export const BuilderToast = ({
  children,
  tone = "info",
  onDismiss,
  testId,
}: {
  children: ReactNode;
  tone?: "info" | "error";
  onDismiss?: () => void;
  testId?: string;
}) => {
  const isError = tone === "error";
  return (
    <Box
      data-testid={testId}
      role={isError ? "alert" : "status"}
      onClick={isError ? undefined : onDismiss}
      sx={{
        position: "fixed",
        bottom: "20px",
        insetInlineEnd: "20px",
        zIndex: 1500,
        display: "flex",
        alignItems: "center",
        gap: "12px",
        maxWidth: "min(420px, calc(100vw - 40px))",
        padding: isError ? "12px 16px" : "10px 16px",
        borderRadius: v2.rSm,
        fontSize: isError ? "13px" : "12.5px",
        fontWeight: isError ? 600 : 400,
        cursor: isError ? "default" : "pointer",
        background: isError ? v2.overWash : "#141C24",
        border: isError ? `1px solid ${v2.over}` : "none",
        color: isError ? v2.over : "#fff",
        boxShadow: "0 6px 24px rgba(20,28,36,.18)",
      }}
    >
      {children}
      {isError && onDismiss && (
        <TextButton quiet onClick={onDismiss}>
          סגור
        </TextButton>
      )}
    </Box>
  );
};
